/**
 * social-proof.js — Trendyol-style rotating social-proof ticker for the product page.
 *
 * - Numbers are randomized ONCE per product per browser session and cached in
 *   sessionStorage, so revisiting the same product in the same session shows the
 *   SAME numbers (no "trust break" from changing figures on navigation).
 * - Messages rotate upward (slide + fade), one visible at a time, like Trendyol.
 * - Config is passed from Liquid via an inline <script class="sp-config" type="application/json">.
 *
 * Custom element: <social-proof> (no theme/importmap dependency — fully self-contained).
 */
(function () {
  if (window.customElements && customElements.get('social-proof')) return;

  var STORE_KEY_PREFIX = 'mm-social-proof:v1:';
  var _nf = {};

  function fmt(n, locale) {
    try {
      if (!_nf[locale]) _nf[locale] = new Intl.NumberFormat(locale);
      return _nf[locale].format(n);
    } catch (e) {
      return String(n);
    }
  }

  function randInt(min, max) {
    min = Math.ceil(Number(min) || 0);
    max = Math.floor(Number(max) || 0);
    if (max < min) { var t = min; min = max; max = t; }
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  function replaceAll(str, find, rep) {
    return String(str).split(find).join(rep);
  }

  function escapeHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  var proto = Object.create(HTMLElement.prototype);

  function connected(el) {
    if (el._spInit) return;
    el._spInit = true;

    var cfg;
    try {
      var node = el.querySelector('script.sp-config');
      cfg = JSON.parse(node.textContent);
    } catch (e) {
      return;
    }
    el._cfg = cfg;

    var metrics = (cfg.metrics || []).filter(Boolean);
    if (!metrics.length) return;

    var values = resolveValues(el, cfg, metrics);

    // Build the message HTML for each enabled metric (funnel order = config order).
    el._messages = metrics.map(function (m) {
      var numHtml = '<span class="sp__num">' + escapeHtml(fmt(values[m.key], cfg.locale)) + '</span>';
      var text = m.text || '[n]';
      text = replaceAll(text, '[days]', String(cfg.days != null ? cfg.days : 3));
      // Split on [n] so merchant text stays escaped but the number span is injected.
      var parts = text.split('[n]');
      return parts.map(escapeHtml).join(numHtml);
    });

    buildDom(el);
    if (el._messages.length > 1) {
      var interval = Math.max(2000, (Number(cfg.interval) || 4) * 1000);
      startRotation(el, interval);
    }
  }

  function resolveValues(el, cfg, metrics) {
    var key = STORE_KEY_PREFIX + (cfg.productId != null ? cfg.productId : 'default');
    var store = {};
    try { store = JSON.parse(sessionStorage.getItem(key)) || {}; } catch (e) {}
    var values = store.v || {};
    var changed = false;

    metrics.forEach(function (m) {
      var cur = values[m.key];
      // Regenerate if missing OR outside the currently-configured range (settings changed).
      if (typeof cur !== 'number' || cur < m.min || cur > m.max) {
        values[m.key] = randInt(m.min, m.max);
        changed = true;
      }
    });

    if (changed) {
      try {
        sessionStorage.setItem(key, JSON.stringify({ v: values, t: store.t || Date.now() }));
      } catch (e) {}
    }
    return values;
  }

  function makeItem(html) {
    var item = document.createElement('div');
    item.className = 'sp__item';
    item.innerHTML = html;
    return item;
  }

  function buildDom(el) {
    el.classList.add('sp');
    var viewport = document.createElement('div');
    viewport.className = 'sp__viewport';
    viewport.setAttribute('aria-live', 'polite');
    el.appendChild(viewport);
    el._viewport = viewport;

    // Measure the tallest message so the viewport height is stable (handles wrapping on mobile).
    var probe = document.createElement('div');
    probe.className = 'sp__item';
    probe.style.position = 'static';
    probe.style.visibility = 'hidden';
    viewport.appendChild(probe);
    var maxH = 0;
    el._messages.forEach(function (html) {
      probe.innerHTML = html;
      maxH = Math.max(maxH, probe.offsetHeight);
    });
    viewport.removeChild(probe);
    if (maxH) viewport.style.height = maxH + 'px';

    el._index = 0;
    el._current = makeItem(el._messages[0]);
    viewport.appendChild(el._current);
  }

  function startRotation(el, interval) {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    el._timer = setInterval(function () {
      if (document.hidden) return;
      var next = (el._index + 1) % el._messages.length;
      var incoming = makeItem(el._messages[next]);

      if (reduce) {
        el._viewport.innerHTML = '';
        el._viewport.appendChild(incoming);
        el._current = incoming;
        el._index = next;
        return;
      }

      incoming.classList.add('sp__item--enter');
      el._viewport.appendChild(incoming);
      // Force reflow so the enter transition plays.
      void incoming.offsetHeight;

      var outgoing = el._current;
      incoming.classList.remove('sp__item--enter');
      outgoing.classList.add('sp__item--leave');

      var cleaned = false;
      function done() {
        if (cleaned) return;
        cleaned = true;
        outgoing.removeEventListener('transitionend', done);
        if (outgoing.parentNode) outgoing.parentNode.removeChild(outgoing);
      }
      outgoing.addEventListener('transitionend', done);
      setTimeout(done, 900); // fallback if transitionend doesn't fire

      el._current = incoming;
      el._index = next;
    }, interval);
  }

  function disconnected(el) {
    if (el._timer) { clearInterval(el._timer); el._timer = null; }
  }

  // Use class syntax when available; fall back to prototype for very old engines.
  try {
    var SocialProof = /*@__PURE__*/ (function () {
      function C() { return Reflect.construct(HTMLElement, [], C); }
      C.prototype = Object.create(HTMLElement.prototype);
      C.prototype.constructor = C;
      C.prototype.connectedCallback = function () { connected(this); };
      C.prototype.disconnectedCallback = function () { disconnected(this); };
      Object.setPrototypeOf(C, HTMLElement);
      return C;
    })();
    customElements.define('social-proof', SocialProof);
  } catch (e) {
    // Extremely old browser without custom elements — no-op (progressive enhancement).
  }
})();
